import { Plus } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { api, type Incident, type IncidentSeverity, type NewIncident } from '../../api';
import { Field, FilterBar, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useRetained, useWrite, WriteError } from '../../components/Write';
import { useApi } from '../../lib/useApi';
import { incidentPath, IncidentTable, RefreshButton, SEVERITY_ORDER, useEnumLabel } from './shared';

const isSeverity = (s: string | null): s is IncidentSeverity => s !== null && (SEVERITY_ORDER as readonly string[]).includes(s);

type StatusFilter = 'active' | 'resolved' | 'all';

const splitList = (s: string): string[] =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

function OpenIncidentForm({ onDone, onCancel }: { onDone: (i: Incident) => void; onCancel: () => void }) {
  const { t } = useTranslation('ops');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const [params] = useSearchParams();
  const id = useId();
  const initialSeverity = params.get('severity');
  const [severity, setSeverity] = useState<IncidentSeverity>(isSeverity(initialSeverity) ? initialSeverity : 'P2');
  const [title, setTitle] = useState(params.get('title') ?? '');
  const [alerts, setAlerts] = useState(params.get('alert') ?? '');
  const [services, setServices] = useState(params.get('service') ?? '');
  const write = useWrite((req: NewIncident, opts) => api.openIncident(req, opts));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await write.run({ severity, title: title.trim(), related_alerts: splitList(alerts), related_services: splitList(services) });
    if (res) onDone(res);
  };

  return (
    <form onSubmit={submit} className="grid gap-4" aria-labelledby={`${id}-t`}>
      <DialogHeader>
        <DialogTitle id={`${id}-t`}>{t('incidents.form.title')}</DialogTitle>
        <DialogDescription>{t('incidents.form.description')}</DialogDescription>
      </DialogHeader>
      <Field label={t('incidents.form.severity')}>
        <Select value={severity} onValueChange={(v) => setSeverity(v as IncidentSeverity)} disabled={write.busy}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SEVERITY_ORDER.map((s) => (
              <SelectItem key={s} value={s}>
                {s} · {label('severity', s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t('incidents.form.titleLabel')}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} disabled={write.busy} />
      </Field>
      <Field label={t('incidents.form.relatedAlerts')} hint={t('incidents.form.commaHint')}>
        <Input value={alerts} onChange={(e) => setAlerts(e.target.value)} disabled={write.busy} className="font-mono" />
      </Field>
      <Field label={t('incidents.form.relatedServices')} hint={t('incidents.form.commaHint')}>
        <Input value={services} onChange={(e) => setServices(e.target.value)} disabled={write.busy} className="font-mono" />
      </Field>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !title.trim()}>
          {write.busy ? t('incidents.form.opening') : t('incidents.form.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** `/ops/incidents`: the incident log; each row opens the incident (spec §118). */
export function IncidentsPage() {
  const { t } = useTranslation('ops');
  useDocumentTitle(t('incidents.title'));
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const creating = params.get('new') === '1';
  const statusParam = params.get('status');
  const filter: StatusFilter = statusParam === 'resolved' || statusParam === 'all' ? statusParam : 'active';
  const result = useApi((signal) => api.listIncidents({ signal }), []);
  const items = useRetained(result.status === 'success' ? result.data.items : undefined);
  const rows = (items ?? []).filter((i) => filter === 'all' || (filter === 'resolved' ? i.status === 'RESOLVED' : i.status !== 'RESOLVED'));

  const closeDialog = () => {
    const p = new URLSearchParams(params);
    p.delete('new');
    setParams(p, { replace: true });
  };
  const setFilter = (f: string) => {
    if (!f) return;
    const p = new URLSearchParams(params);
    if (f === 'active') p.delete('status');
    else p.set('status', f);
    setParams(p, { replace: true });
  };

  return (
    <>
      <PageHeader
        title={t('incidents.title')}
        subtitle={t('incidents.subtitle')}
        actions={
          <>
            <RefreshButton onClick={result.reload} busy={result.status === 'loading'} />
            <Button
              size="sm"
              onClick={() => {
                const p = new URLSearchParams(params);
                p.set('new', '1');
                setParams(p);
              }}
              disabled={creating}
            >
              <Plus /> {t('incidents.open')}
            </Button>
          </>
        }
      />
      <Dialog open={creating} onOpenChange={(open) => (open ? undefined : closeDialog())}>
        <DialogContent>
          {creating ? <OpenIncidentForm onDone={(i) => navigate(incidentPath(i.id))} onCancel={closeDialog} /> : null}
        </DialogContent>
      </Dialog>
      <FilterBar actions={items?.length ? <span className="text-sm text-muted-foreground">{t('incidents.openHint')}</span> : undefined}>
        <Field label={t('incidents.filter.status')}>
          <ToggleGroup type="single" variant="outline" size="sm" value={filter} onValueChange={setFilter}>
            {(['active', 'resolved', 'all'] as const).map((f) => (
              <ToggleGroupItem key={f} value={f}>
                {t(`incidents.filter.${f}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </Field>
      </FilterBar>
      <Section flush>
        {result.status === 'loading' && !items ? (
          <div className="px-5">
            <Loading rows={4} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('incidents.loadFailed')} />
          </div>
        ) : null}
        {items ? <IncidentTable rows={rows} empty={<EmptyState title={items.length ? t('incidents.emptyFiltered') : t('incidents.empty')} />} /> : null}
      </Section>
    </>
  );
}
